<template>
  <div class="reserve-service-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('reserveServiceManage.reserveService')"
      :subtitle="$t('reserveServiceManage.page.subtitle')" />

    <el-row :gutter="16">
      <!-- 左侧：目录树 -->
      <el-col :xs="24" :md="6" :lg="5">
        <div class="ds-panel ds-panel--padded">
          <div class="ds-panel__header">
            <span class="ds-panel__title">{{ $t('reserveServiceManage.addCatalog') }}</span>
          </div>
          <ul v-if="reserveServiceManageInfo.catalogs && reserveServiceManageInfo.catalogs.length > 0" class="ds-tree">
            <template v-for="(item, index) in reserveServiceManageInfo.catalogs">
              <li :key="index" class="ds-tree__item"
                :class="{ 'ds-tree__item--active': reserveServiceManageInfo.conditions.catalogId == item.catalogId }"
                @click="swatchReserveCatalog(item)">
                {{ item.name }}
              </li>
            </template>
          </ul>
          <el-empty v-else :description="$t('reserveServiceManage.page.noCatalog')" :image-size="60" />
          <el-button type="text" icon="el-icon-plus" class="ds-tree__add" @click="_addCatalog">
            {{ $t('reserveServiceManage.addCatalog') }}
          </el-button>
        </div>
      </el-col>

      <!-- 右侧：查询 + 列表 -->
      <el-col :xs="24" :md="18" :lg="19">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="reserveServiceManageInfo.conditions.goodsId" clearable
                :placeholder="$t('reserveServiceManage.goodsIdPlaceholder')"
                @keyup.enter.native="_queryReserveServiceMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="reserveServiceManageInfo.conditions.goodsName" clearable
                :placeholder="$t('reserveServiceManage.goodsNamePlaceholder')"
                @keyup.enter.native="_queryReserveServiceMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="reserveServiceManageInfo.conditions.state" clearable class="w-full"
                :placeholder="$t('reserveServiceManage.statePlaceholder')">
                <el-option :label="$t('reserveServiceManage.stateOffShelf')" value="1001" />
                <el-option :label="$t('reserveServiceManage.stateOnShelf')" value="2002" />
              </el-select>
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="_queryReserveServiceMethod">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="_resetReserveServiceMethod">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="_queryReserveServiceMethod">
            <template #left>
              <span class="ds-panel__title">{{ $t('reserveServiceManage.reserveService') }}</span>
            </template>
            <template #right>
              <el-button type="primary" size="mini" icon="el-icon-plus" @click="_openAddReserveServiceModal">
                {{ $t('reserveServiceManage.addService') }}
              </el-button>
            </template>
          </ds-toolbar>

          <ds-data-table :data="reserveServiceManageInfo.reserveServices" :loading="loading" :error="hasError"
            :error-message="errorMessage" :density="density" border
            :empty-description="$t('reserveServiceManage.page.empty')" @retry="_queryReserveServiceMethod">
            <el-table-column :label="$t('reserveServiceManage.serviceCover')" align="center" width="90">
              <template slot-scope="scope">
                <el-image class="ds-cover" :src="scope.row.imgUrl || '/img/noPhoto.jpg'" fit="cover" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('reserveServiceManage.nameAndId')" align="left" min-width="180" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.goodsName }}<span class="ds-num">({{ scope.row.goodsId }})</span>
              </template>
            </el-table-column>
            <el-table-column prop="paramsName" :label="$t('reserveServiceManage.params')" align="center" min-width="120" show-overflow-tooltip />
            <el-table-column :label="$t('reserveServiceManage.reserveWay')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag :label="scope.row.paramWay == '1' ? $t('reserveServiceManage.day') : $t('reserveServiceManage.week')"
                  :text="scope.row.paramWay == '1' ? $t('reserveServiceManage.day') : $t('reserveServiceManage.week')" type="info" />
              </template>
            </el-table-column>
            <el-table-column prop="maxQuantity" :label="$t('reserveServiceManage.reserveCount')" align="right" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.maxQuantity }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="hoursMaxQuantity" :label="$t('reserveServiceManage.reserveQuantity')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.hoursMaxQuantity }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="price" :label="$t('reserveServiceManage.doorFee')" align="right" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.price }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('reserveServiceManage.validity')" align="center" width="200">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.startDate }}</span><br /><span class="ds-num">~{{ scope.row.endDate }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="startTime" :label="$t('reserveServiceManage.reserveStartTime')" align="center" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.startTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="sort" :label="$t('reserveServiceManage.sort')" align="center" width="90">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.sort }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('reserveServiceManage.state')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag :label="scope.row.state == '1001' ? $t('reserveServiceManage.offShelf') : $t('reserveServiceManage.onShelf')"
                  :text="scope.row.state == '1001' ? $t('reserveServiceManage.offShelf') : $t('reserveServiceManage.onShelf')"
                  :type="scope.row.state == '1001' ? 'info' : 'success'" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="230" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" type="text" @click="_openReserveServicePersonModel(scope.row)">
                  {{ $t('reserveServiceManage.reserveService') }}
                </el-button>
                <el-button size="mini" type="text" @click="_openEditReserveServiceModel(scope.row)">
                  {{ $t('common.edit') }}
                </el-button>
                <el-button size="mini" type="text" class="ds-btn-danger" @click="_openDeleteReserveServiceModel(scope.row)">
                  {{ $t('common.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
            @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>

    <add-reserve-service-person ref="addReserveServicePerson" />
    <delete-reserve-dining ref="deleteReserveDining" @success="handleDeleteSuccess" />
  </div>
</template>

<script>
import { listReserveGoods, listReserveCatalog } from '@/api/scm/reserveServiceManageApi'
import { getCommunityId } from '@/api/community/communityApi'
import AddReserveServicePerson from '@/components/scm/addReserveServicePerson'
import DeleteReserveDining from '@/components/scm/deleteReserveDining'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ReserveServiceManageList',
  components: {
    AddReserveServicePerson,
    DeleteReserveDining,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      reserveServiceManageInfo: {
        reserveServices: [],
        catalogs: [],
        conditions: {
          goodsId: '',
          goodsName: '',
          type: '2002',
          state: '',
          catalogId: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.reserveServiceManageInfo.conditions.communityId = getCommunityId()
    this._listReserveCatalogs()
  },
  methods: {
    async _listReserveServices(page, rows) {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        this.reserveServiceManageInfo.conditions.page = page
        this.reserveServiceManageInfo.conditions.row = rows
        const { data, total } = await listReserveGoods(this.reserveServiceManageInfo.conditions)
        this.reserveServiceManageInfo.reserveServices = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('获取预约服务列表失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('reserveServiceManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    async _listReserveCatalogs() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.reserveServiceManageInfo.conditions.communityId,
          type: '2002'
        }
        const { data } = await listReserveCatalog(params)
        this.reserveServiceManageInfo.catalogs = Array.isArray(data) ? data : []
        if (data && data.length > 0) {
          this.swatchReserveCatalog(data[0])
        }
      } catch (error) {
        console.error('获取预约目录失败:', error)
      }
    },
    _openAddReserveServiceModal() {
      if (!this.reserveServiceManageInfo.conditions.catalogId) {
        this.$message.warning(this.$t('reserveServiceManage.addCatalogFirst'))
        return
      }
      this.$router.push(`/views/scm/addReserveService?catalogId=${this.reserveServiceManageInfo.conditions.catalogId}`)
    },
    _openEditReserveServiceModel(reserveService) {
      this.$router.push(`/views/scm/editReserveService?goodsId=${reserveService.goodsId}`)
    },
    _openDeleteReserveServiceModel(reserveService) {
      this.$refs.deleteReserveDining.open(reserveService)
    },
    _queryReserveServiceMethod() {
      this._listReserveServices(1, this.page.size)
    },
    _resetReserveServiceMethod() {
      this.reserveServiceManageInfo.conditions.goodsId = ''
      this.reserveServiceManageInfo.conditions.goodsName = ''
      this.reserveServiceManageInfo.conditions.state = ''
      this.page.current = 1
      this._listReserveServices(this.page.current, this.page.size)
    },
    _openReserveServicePersonModel(reserveService) {
      this.$refs.addReserveServicePerson.open(reserveService)
    },
    swatchReserveCatalog(item) {
      this.reserveServiceManageInfo.conditions.catalogId = item.catalogId
      this._listReserveServices(1, this.page.size)
    },
    _addCatalog() {
      this.$router.push('/pages/reserve/reserveCatalogManage?tab=reserveCatalog')
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this._listReserveServices(this.page.current, val)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listReserveServices(val, this.page.size)
    },
    handleDeleteSuccess() {
      this._listReserveServices(1, this.page.size)
    }
  }
}
</script>

<style lang="scss" scoped>
.reserve-service-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &--padded {
    padding: var(--ds-spacing-lg);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-tree {
  list-style: none;
  padding: 0;
  margin: 0;

  &__item {
    padding: 9px 12px;
    border: 1px solid var(--ds-gray-300);
    margin-bottom: -1px;
    cursor: pointer;
    font-size: var(--ds-font-size-md);
    color: var(--ds-gray-700);
    transition: background-color 0.2s, color 0.2s;

    &:first-child {
      border-radius: var(--ds-radius-sm) var(--ds-radius-sm) 0 0;
    }

    &:last-child {
      border-radius: 0 0 var(--ds-radius-sm) var(--ds-radius-sm);
    }

    &:hover {
      background-color: var(--ds-gray-100);
    }

    &--active {
      background-color: var(--ds-primary);
      color: #fff;
      border-color: var(--ds-primary);
    }
  }

  &__add {
    margin-top: var(--ds-spacing-sm);
    padding: 0;
  }
}

.ds-cover {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  display: block;
  margin: 0 auto;
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
